import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useLocation, useHistory, useRouteMatch } from "react-router-dom";

import {
  createEditorSession,
  createWidgetFromHTML,
  createWidgetsForPromptSections
} from "actions/editor";

import {
  fetchPage,
  updatePage,
  restorePage,
  invalidatePageState,
  startAutoSavePageTask,
  stopAutoSavePageTask,
} from "actions/pages";

import { fetchIntegrations } from "actions/integrations/integrations";
import * as widgetTypes from "constants/widgetTypes";

import _ from "lodash";
import cx from "classnames";
import { toast } from "react-toastify";

import StoryboardContext from "components/Storyboard/context";
import { SuccessToast, FailureToast } from "components/Toast";
import { Editor } from "components/Editor";
import { Loader } from "components/ApplicationLoader";
import { PageHeader, HeaderContext } from "./components/header";

import { PAGE, SLIDE } from "constants/widgetTypes";
import { PROMPT_EDITOR_DATA, PROMPT_EDITOR_DATA_REQUEST } from "constants/postMessageTypes";
import { SEGMENT_STORYBOARDS } from "constants/storyboardType";

import { EditorLockedDialog, LockManager } from "components/LockManager";
import LockService from "services/lock.service";
import storyboardRoleService from "services/storyboardRoles.service";

import { fetchCustomFields } from 'actions/fields/fields';
import { getPageViewedEventPayload } from "services/mixpanelUtil";
import { MIXPANEL_PAGE_VIEWED, MIXPANEL_PAGE_VIEWED_EDITOR } from "constants/mixpanelEvents";
import { createMixpanelEvent } from "actions/mixpanel";
import { fetchVariables } from "actions/variables";

import styles from "./pages.module.scss";

import { getBaseUrl } from "services/util";
import { useCallback } from "react";
import { useMemo } from "react";

export default function PageView(props) {
  const dispatch = useDispatch();

  const { id } = useParams();
  const { search } = useLocation();
  const urlParams = new URLSearchParams(search);
  const isPromptEditor = urlParams.get('isPromptEditor') === 'true';
  const history = useHistory();
  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);

  const [headerContext, setHeaderContext] = useState({
    type: 'page',
    entity: { },
    editorSession: null,
    optimizeLayout: false,
    updateState: {},
    autoSaveState: {},
    onSave: () => {},
    onRestore: () => {}
  });
  const [isStable, setIsStable] = useState(false);

  const page = useSelector(state => _.get(state, `pages.page.${id}.item`));
  const pageError = useSelector(state => _.get(state, `pages.page.${id}.error`));
  const pageLoaded = _.get(page, "id");
  const loadingPage = useSelector(state => _.get(state, `pages.page.${id}.loading`, false));
  const cloningPage = useSelector(state => _.get(state, `pages.cloning`, false));
  const editorSession = React.useRef(`editors_${id}`);
  const homePageIdFromPage = _.get(page, "storyboard.page.id");
  const homePageIdFromStoryboard = useSelector(state => _.get(state, "storyboards.storyboard.item.page.id"));
  const homePageId = homePageIdFromPage || homePageIdFromStoryboard;

  useEffect(() => {
    if (pageError && homePageId && homePageId !== id) {
      history.push(`${baseUrl}/pages/${homePageId}`);
    }
  }, [pageError, homePageId, id, history, baseUrl]);

  const updateState = useSelector((state) => _.get(state, `pages.page.${id}.update`));
  const autoSaveState = useSelector((state) => _.get(state, `pages.page.${id}.autoSave`));

  const restoreState = useSelector((state) => _.get(state, `pages.page.${id}.restore`));
  const restoringVersion = useSelector(state=> _.get(state, 'versions.restoring', false));
  const widgets = useSelector(state => _.get(state, `${editorSession.current}.present.widgets`, []));
  const activePage = useSelector(state => _.get(state, `${editorSession.current}.present.interactive.active`, null));
  const mobileOptimizationPage = useSelector(state => _.get(state, `${editorSession.current}.present.mobileOptimization.pageWidgets.${activePage}`, null));
  const activePageOptimizing = _.get(mobileOptimizationPage, 'optimizing', false);
  const activePageView = _.get(mobileOptimizationPage, 'view', 'desktop');
  
  const pageWidgets = useMemo(
    () => widgets.filter(item => item.type === widgetTypes.PAGE),
    [widgets]
  );
  const pageWidgetUids = useMemo(
    () => pageWidgets.map(item => item.uid),
    [pageWidgets]
  );

  const isOptimizing = useSelector(state =>
    pageWidgetUids.some(uid =>
      _.get(state, `${editorSession.current}.present.mobileOptimization.pageWidgets.${uid}.optimizing`, false)
    )
  );
  const isPromptProcessing = useSelector(state =>
    _.get(state, `${editorSession.current}.present.promptProcessing`, false)
  );

  const view = useSelector(state =>
    pageWidgetUids.every(uid =>
      _.get(state, `${editorSession.current}.present.mobileOptimization.pageWidgets.${uid}.view`, "desktop") === "mobile"
    ) ? "mobile" : "desktop"
  );

  const storyboard = useMemo(() => _.get(page, "storyboard"), [page]);
  const storyboardId = _.get(storyboard, 'id', '');
  const storyboardName = _.get(storyboard, 'name', '');
  const storyboardType = _.get(storyboard, 'type', '');

  const storyboardItemFromSlice = useSelector((state) =>
    _.get(state, 'storyboards.storyboard.item')
  );

  const themes = _.get(storyboard, 'themes', []);
  const appearance = useMemo(() => {
    const pageSbId = _.get(page, 'storyboard.id');
    const sliceId = _.get(storyboardItemFromSlice, 'id');
    if (pageSbId && sliceId && String(pageSbId) === String(sliceId)) {
      return _.get(storyboardItemFromSlice, 'appearance', {});
    }
    return _.get(page, 'storyboard.appearance', {});
  }, [page, storyboardItemFromSlice]);
  const brandTheme = _.get(storyboard, 'brandTheme', []);

  const reference = React.useMemo(() => {
    return {
      type: 'page',
      id: id
    }
  }, [id]);
  
  const lock = useSelector(state => _.get(state, `pages.page.${id}.item.lock`)) || null;
  
  const lockedByMe = React.useMemo(() => {
    return !_.isNull(lock) && _.get(lock, 'token') === LockService.getLock(reference);
  }, [lock, reference]);

  const lockError = useSelector(state => {
    const error = _.get(state, `pages.page.${id}.update.error.errors.0`);

    if(_.get(error, 'code') === 36002){
      return error;
    }

    return null;
  });

  const canBreakLock = React.useMemo(() => {
      return storyboardRoleService.canEditStoryboard(storyboard);
  }, [storyboard]);

  const isInteractiveContent = storyboardType === 'INTERACTIVE_CONTENT';
  
  const optimizingState = isInteractiveContent
    ? activePageOptimizing
    : isOptimizing;
  
  const currentView = isInteractiveContent
    ? activePageView
    : view;
  
  const isDisabled = !lockedByMe || optimizingState;
  
  const isEditorDisabled = isInteractiveContent
    ? (!lockedByMe || optimizingState)
    : !lockedByMe;


  useEffect(() => {
    // Non-lazy fetch: PAGE-LAZY-LOAD omits/truncates draft.widgets (e.g. multi-slide
    // spatial/flipbook pages), which breaks both the canvas and Pages & Slides after refresh.
    dispatch(fetchPage(id, false));
    dispatch(fetchIntegrations());

    if(storyboard && _.get(storyboard, 'type') === "RESOURCE_CENTER") {
      dispatch(fetchCustomFields(_.get(storyboard, 'id')));
    }

    editorSession.current = `editors_${id}`;

    return () => {
      dispatch(stopAutoSavePageTask(id));
    }
  }, [id]);

  useEffect(() => {
    if(pageLoaded){
      var mixPanelPayload = getPageViewedEventPayload(MIXPANEL_PAGE_VIEWED_EDITOR, storyboardId, storyboardName, storyboardType)
      mixPanelPayload = {
        ...mixPanelPayload,
        editor_type: 'Page',
        device_type: 'Desktop'
      }
      dispatch(createMixpanelEvent(MIXPANEL_PAGE_VIEWED, mixPanelPayload))
    }
  }, [storyboard]);

  useEffect(() => {
    if (editorSession.current === `editors_${pageLoaded}` && !loadingPage) {
      initialiseEditor();
      if (isPromptEditor) {
        const opener = window.opener;

        let openerIsSameOrigin = false;
        let openedFromAnotherTab = false;
        if (opener) {
          openedFromAnotherTab = true;
          try {
            // eslint-disable-next-line no-unused-expressions
            opener.location.href;
            openerIsSameOrigin = true;
          } catch {
            openerIsSameOrigin = false;
          }
        }

        if (openedFromAnotherTab && !openerIsSameOrigin) {
          const expectedOrigin = process.env.REACT_APP_PAPERFLITE_APP_URL;
          const targetOrigin = expectedOrigin || '*';
          const sessionRef = editorSession.current;

          const handler = (event) => {
            if (expectedOrigin && event.origin !== expectedOrigin) return;
            if (event.data?.type !== PROMPT_EDITOR_DATA) return;

            const payload = event.data.payload;
            const hasValidSectionData = payload?.pageStructure?.sections?.length > 0;
            if (hasValidSectionData) {
              dispatch(createWidgetFromHTML(sessionRef, payload, storyboardId));
            }
            window.removeEventListener('message', handler);
            clearTimeout(timeoutId);
          };

          window.addEventListener('message', handler);
          opener.postMessage({ type: PROMPT_EDITOR_DATA_REQUEST }, targetOrigin);

          const timeoutId = setTimeout(() => {
            window.removeEventListener('message', handler);
            console.warn('promptEditorData handshake timed out');
          }, 5000);
        } else {
          const sectionDataStr = localStorage.getItem('promptEditorData');
          let parsedSectionData = null;
          try {
            parsedSectionData = sectionDataStr ? JSON.parse(sectionDataStr) : null;
          } catch {
            parsedSectionData = null;
          }
          const hasValidSectionData = parsedSectionData?.pageStructure?.sections?.length > 0;
          if (hasValidSectionData) {
            dispatch(createWidgetFromHTML(editorSession.current, parsedSectionData, storyboardId));
          }
        }
      }
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: page,
          editorSession: editorSession.current,
          updateState: updateState,
          autoSaveState: autoSaveState,
          onSave: handlePageSave,
          onRestore: handlePageRestore
        }
      })
    }
  }, [editorSession.current, pageLoaded, loadingPage]);

  useEffect(() => {
    const ready = pageLoaded && !loadingPage && !cloningPage && !restoringVersion;
    if (ready) {
      const timer = setTimeout(() => setIsStable(true), 100);
      return () => clearTimeout(timer);
    } else {
      setIsStable(false);
    }
  }, [pageLoaded, loadingPage, cloningPage, restoringVersion]);

  useEffect(() => {
    if (page) {
      setHeaderContext(headerContext => {
        return {
          ...headerContext,
          entity: page,
        }
      })
    }
  }, [page]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        updateState: updateState
      }
    })

    if (!updateState) {
      return;
    }

    if (updateState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} saved successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePageState(page.id));
    } else if (updateState.error) {
      if(_.get(updateState.error, 'errors.0.code') !== 36002){
        toast(
          <FailureToast
            heading={`Failure`}
            description={`Unable to save ${renderToastName()} page`}
          />,
          {
            type: toast.TYPE.DEFAULT,
            position: toast.POSITION.BOTTOM_RIGHT,
            autoClose: 3000,
          }
        );

        dispatch(invalidatePageState(page.id));
      }
    }
  }, [updateState]);

  useEffect(() => {
    if (restoreState && restoreState.success) {
      initialiseEditor();
    }

    if (!restoreState) {
      return;
    }

    if (restoreState.success) {
      toast(
        <SuccessToast
          heading={`Success`}
          description={`${renderToastName()} restored successfully`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePageState(page.id));
    } else if (restoreState.error) {
      toast(
        <FailureToast
          heading={`Failure`}
          description={`Unable to restore ${renderToastName()} page`}
        />,
        {
          type: toast.TYPE.DEFAULT,
          position: toast.POSITION.BOTTOM_RIGHT,
          autoClose: 3000,
        }
      );

      dispatch(invalidatePageState(page.id));
    }
  }, [restoreState]);

  useEffect(() => {
    if(lockedByMe){
      dispatch(startAutoSavePageTask(id, editorSession.current));
    }

    return () => {
      dispatch(stopAutoSavePageTask(id));
    }
  }, [id, lockedByMe]);

  useEffect(() => {
    setHeaderContext(headerContext => {
      return {
        ...headerContext,
        autoSaveState: autoSaveState
      }
    });
  }, [autoSaveState]);

  const initialiseEditor = () => {
    const desktopGuides = _.get(page, "draft.desktop.guides");
    const mobileGuides = _.get(page, "draft.mobile.guides");
    let widgets = _.get(page, "draft.widgets") || [];
    const fonts = _.get(page, "draft.fonts") || [];

    if (SEGMENT_STORYBOARDS.includes(storyboardType)) {
      const rootPageWidgets = widgets.filter(w => w.type === PAGE && !w.parent);
      const hasVisibleFrame = rootPageWidgets.some(
        w => _.get(w, 'properties.desktop.visible', true) !== false
      );
      if (rootPageWidgets.length > 0 && !hasVisibleFrame) {
        const firstUid = rootPageWidgets[0].uid;
        widgets = widgets.map(w => {
          if (w.uid !== firstUid) return w;
          return {
            ...w,
            properties: {
              ...w.properties,
              desktop: { ..._.get(w, 'properties.desktop', {}), visible: true }
            }
          };
        });
      }
    }

    var activePage = null;
    var pageSections = widgets.filter((item) => {
      return item.type === PAGE || item.type === SLIDE;
    });

    pageSections = _.sortBy(pageSections, "order");

    if (_.size(pageSections) > 0) {
      activePage = pageSections[0];
    }

    const initialState = {
      desktop: {
        guides: desktopGuides
      },
      mobile: {
        guides: mobileGuides
      },
      widgets: widgets,
      interactive: {
        active: _.get(activePage, 'uid'),
      },
      entityType: 'PAGE',
      themes: themes,
      appearance: appearance,
      brandTheme: brandTheme,
      fonts: fonts
    }

    dispatch(createEditorSession(editorSession.current, initialState));
    var request = {
      storyboard: storyboardId
    }
    dispatch(fetchVariables(request, 0, 50));
  }

  const handlePageSave = useCallback(() => {
    if(!page?.id) return;
    dispatch(updatePage(page.id, editorSession.current)); 
  }, [dispatch, page?.id, editorSession.current]);

  const handlePageRestore = useCallback(() => {
    if(!page?.id) return;
    dispatch(restorePage(page.id, editorSession));
  }, [dispatch, page?.id, editorSession.current]);

  const updateOptimzeLayout = useCallback(() => {
    setHeaderContext(headerContext => ({
      ...headerContext,
      optimizeLayout: true
    }));
  
    setTimeout(() => {
      setHeaderContext(headerContext => ({
        ...headerContext,
        optimizeLayout: false
      }));
    }, 1000);
  }, []);

  const renderToastName = () => {
    var name = _.get(page, 'storyboard.name');

    if (storyboardType !== 'INTERACTIVE_CONTENT') {
      name = _.get(page, 'name');
    }

    return name;
  }

  const renderPageState = () => {
    if(cloningPage){
      return "Cloning Page..."
    }
    else if(restoringVersion){
      return "Restoring Page..."
    }
    else{
      return "Loading Editor..."
    }
  }

  if (loadingPage && storyboardType !== 'INTERACTIVE_CONTENT') {
    return <Loader className={cx(styles.pageLoader, styles.loadingPage)} title={renderPageState()}/>;
  }

  return (
    <StoryboardContext.Provider value={storyboard}>
      <div className={cx(styles.page, { [styles.loadingPage]: loadingPage || !pageLoaded })}>
        <Loader className={cx(styles.pageLoader, { [styles.loadingPage]: loadingPage || !pageLoaded || cloningPage || restoringVersion })} title={renderPageState()}/>
        {reference && pageLoaded && !loadingPage &&
          <LockManager reference={reference} lock={lock} canBreakLock={canBreakLock} isStable={isStable}/>
        }
        
        <HeaderContext.Provider value={headerContext}>
          <PageHeader disabled={isDisabled || isPromptProcessing} editorSession={editorSession.current} page={page} reference={reference} updateOptimzeLayout={updateOptimzeLayout} isOptimizing={optimizingState} />

          <Editor key={editorSession.current} id={editorSession.current} disabled={isEditorDisabled || isPromptProcessing} isOptimizing={optimizingState} view={currentView}/>
        </HeaderContext.Provider>
      </div>

      {lockError &&
        <EditorLockedDialog error={lockError} onClose={() => {
          dispatch(invalidatePageState(page.id));
        }}/>      
      }
    </StoryboardContext.Provider>
  );
}